"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.userUploadApplicationSchema = exports.userUploadRowResultSchema = exports.USER_UPLOAD_ROW_OUTCOMES = exports.userUploadPreviewSchema = exports.userUploadPersonSchema = exports.USER_UPLOAD_STATUSES = exports.userUploadRefusalSchema = exports.userUploadPersonStateSchema = exports.userUploadBodySchema = exports.userUploadApplyHeadersSchema = exports.userUploadPreviewHeadersSchema = exports.USER_UPLOAD_REQUIRED_COLUMNS = exports.USER_UPLOAD_COLUMNS = exports.USER_UPLOAD_SHEET = exports.USER_UPLOAD_PREVIEW_TOKEN_HEADER = exports.USER_UPLOAD_FILE_NAME_HEADER = exports.USER_UPLOAD_PREVIEW_MINUTES = exports.USER_UPLOAD_MAX_BYTES = exports.USER_UPLOAD_MAX_ROWS = void 0;
// J-409 (the owner's specification of 2026-11-02, step 5): Users & Access "Upload users", served under `/api/v1`:
//   GET  /user-uploads/template      the template workbook: the nine columns, the Groups sheet, drop-downs
//   POST /user-uploads/previews      the file (.xlsx or .csv) as `application/octet-stream`, its name percent-encoded
//                                    in `x-file-name`: the plan per person, nothing written, and a preview token
//   POST /user-uploads/applications  the same file and the token in `x-preview-token`: re-checked, applied per
//                                    person, and each row's outcome
// The file is never stored. The answers carry staff e-mail addresses and names (the admin's own Users & Access
// list shows the same), organization identifiers and counts; never a password. No shape carries patient data.
const zod_1 = require("zod");
/** The most rows a file may hold below its header row (blank rows are not counted). */
exports.USER_UPLOAD_MAX_ROWS = 2_000;
/** The largest file accepted; 2,000 rows of nine short columns is far below it. */
exports.USER_UPLOAD_MAX_BYTES = 5_000_000;
/** A preview older than this is refused at Apply. */
exports.USER_UPLOAD_PREVIEW_MINUTES = 30;
/** The request header that carries the file's name, `encodeURIComponent` of the name: never in the URL. */
exports.USER_UPLOAD_FILE_NAME_HEADER = "x-file-name";
/** The request header that carries the preview's token at Apply. */
exports.USER_UPLOAD_PREVIEW_TOKEN_HEADER = "x-preview-token";
/** The sheet read first; without it, the workbook's first sheet. */
exports.USER_UPLOAD_SHEET = "Users";
/** The template's columns, in its order; a file may hold them in any order (case and spaces ignored). */
exports.USER_UPLOAD_COLUMNS = [
    "Performance Group",
    "TIN",
    "ACO",
    "PHI",
    "Gap Workbook",
    "Email Address",
    "Name",
    "Role",
    "Ban/Reactivate",
];
/** The columns every file must hold; Role and Ban/Reactivate are optional. */
exports.USER_UPLOAD_REQUIRED_COLUMNS = exports.USER_UPLOAD_COLUMNS.slice(0, 7);
const fileNameHeader = zod_1.z
    .string()
    .min(1)
    .max(2048)
    .regex(/^[\x21-\x7e]+$/, "a percent-encoded file name");
/** Loose: Fastify replaces the request's headers with the parsed value, and a strict object would drop the rest. */
exports.userUploadPreviewHeadersSchema = zod_1.z.looseObject({
    [exports.USER_UPLOAD_FILE_NAME_HEADER]: fileNameHeader,
});
exports.userUploadApplyHeadersSchema = zod_1.z.looseObject({
    [exports.USER_UPLOAD_FILE_NAME_HEADER]: fileNameHeader,
    [exports.USER_UPLOAD_PREVIEW_TOKEN_HEADER]: zod_1.z
        .string()
        .min(1)
        .max(512)
        .regex(/^[A-Za-z0-9._-]+$/, "a preview token"),
});
/** The file's bytes, `application/octet-stream`; the route's bodyLimit is `USER_UPLOAD_MAX_BYTES`. */
exports.userUploadBodySchema = zod_1.z
    .custom((value) => Buffer.isBuffer(value) && value.length > 0, "a non-empty file")
    .meta({ type: "string", format: "binary" });
/** One person's account and access as the upload sees it, before or after. */
exports.userUploadPersonStateSchema = zod_1.z
    .object({
    name: zod_1.z.string(),
    /** The role id (`practice_manager`, `practice_staff`, or the role an existing account holds). */
    role: zod_1.z.string(),
    banned: zod_1.z.boolean(),
    phiAccess: zod_1.z.boolean(),
    gapClosureWorkbook: zod_1.z.boolean(),
    /** This performance year's group grants, by group name, sorted. */
    groups: zod_1.z.array(zod_1.z.string()),
    /** The practice grants, by ACO code then TIN. */
    tins: zod_1.z.array(zod_1.z.object({ acoCode: zod_1.z.string(), tin: zod_1.z.string() })),
})
    .meta({ id: "UserUploadPersonState" });
/** A refusal, with the file's row number it is about (null when it is about the person as a whole). */
exports.userUploadRefusalSchema = zod_1.z.object({
    row: zod_1.z.number().int().nullable(),
    message: zod_1.z.string(),
});
exports.USER_UPLOAD_STATUSES = ["new", "updated", "no_change", "refused"];
/** One person of the file: what the upload would do to them. */
exports.userUploadPersonSchema = zod_1.z
    .object({
    email: zod_1.z.string(),
    /** The existing account's id; null for a new person. */
    userId: zod_1.z.string().nullable(),
    status: zod_1.z.enum(exports.USER_UPLOAD_STATUSES),
    /** The file's row numbers of this person, ascending. */
    rows: zod_1.z.array(zod_1.z.number().int()),
    refusals: zod_1.z.array(exports.userUploadRefusalSchema),
    /** The account as it is; null for a new person. */
    before: exports.userUploadPersonStateSchema.nullable(),
    /** The account as the upload leaves it; null for a refused person. */
    after: exports.userUploadPersonStateSchema.nullable(),
    /** The names of the fields that change, in a fixed order. */
    changes: zod_1.z.array(zod_1.z.string()),
    /** Whether the account is banned or reactivated by the upload. */
    banChange: zod_1.z.enum(["banned", "reactivated"]).nullable(),
})
    .meta({ id: "UserUploadPerson" });
const totalsSchema = zod_1.z.object({
    new: zod_1.z.number().int(),
    updated: zod_1.z.number().int(),
    noChange: zod_1.z.number().int(),
    refused: zod_1.z.number().int(),
});
/** The preview: nothing was written. */
exports.userUploadPreviewSchema = zod_1.z
    .object({
    performanceYear: zod_1.z.number().int().nullable(),
    /** The file's rows below the header, blank rows left out. */
    rowCount: zod_1.z.number().int(),
    people: zod_1.z.array(exports.userUploadPersonSchema),
    /** Rows that belong to no person (no valid e-mail address), refused. */
    rowRefusals: zod_1.z.array(exports.userUploadRefusalSchema),
    totals: totalsSchema,
    /** People whose PHI access the upload turns on (new people with PHI On included). */
    phiTurnedOn: zod_1.z.number().int(),
    banned: zod_1.z.number().int(),
    reactivated: zod_1.z.number().int(),
    /** Sent back at Apply with the same file, within `USER_UPLOAD_PREVIEW_MINUTES`. */
    token: zod_1.z.string(),
    expiresAt: zod_1.z.iso.datetime({ offset: true }),
})
    .meta({ id: "UserUploadPreview" });
exports.USER_UPLOAD_ROW_OUTCOMES = [
    "created",
    "updated",
    "no_change",
    "refused",
    "failed",
];
/** One row of the file and what Apply did with it: the results download. */
exports.userUploadRowResultSchema = zod_1.z.object({
    row: zod_1.z.number().int(),
    email: zod_1.z.string(),
    outcome: zod_1.z.enum(exports.USER_UPLOAD_ROW_OUTCOMES),
    message: zod_1.z.string(),
});
/** Apply's answer: what was written, per person and per row. */
exports.userUploadApplicationSchema = zod_1.z
    .object({
    performanceYear: zod_1.z.number().int().nullable(),
    rowCount: zod_1.z.number().int(),
    people: zod_1.z.array(exports.userUploadPersonSchema.extend({
        outcome: zod_1.z.enum(exports.USER_UPLOAD_ROW_OUTCOMES),
        message: zod_1.z.string(),
    })),
    rows: zod_1.z.array(exports.userUploadRowResultSchema),
    totals: zod_1.z.object({
        created: zod_1.z.number().int(),
        updated: zod_1.z.number().int(),
        noChange: zod_1.z.number().int(),
        refused: zod_1.z.number().int(),
        failed: zod_1.z.number().int(),
    }),
    /** New accounts whose "account created" e-mail was queued (or sent). */
    emailsQueued: zod_1.z.number().int(),
    /** New accounts whose e-mail could not be queued. */
    emailsFailed: zod_1.z.number().int(),
})
    .meta({ id: "UserUploadApplication" });
//# sourceMappingURL=user-uploads.js.map